/* Customer onboarding (MOS-222): the post-purchase intake, ported from Backbone's /onboarding.
   The resume token in the link IS the capability. It is moved into this tab's session storage
   on load and stripped from the address bar; requests carry only the intake body, never
   attribution, cookies or the page URL. */
(() => {
  const config = window.__MOSES_ONBOARDING__;
  const shared = window.MosesQuizForm;
  if (!config || !shared) return;
  // The request, phone/email checks, analytics hook and screen pieces are shared with the quiz
  // and the partners page (assets/quiz-form.js, loaded before this script).
  const { formatPhone, emailValid, phoneValid, track, transition, typographic, progress, SUBMIT_CLASS, MESSAGES, contactLine, steps } = shared;
  const request = (path, options) => shared.request(config.apiBase, path, options);
  const KEY = "moses_onboarding_v1";
  const TOTAL_STEPS = 5;
  const POLL_START_MS = 1500;
  const POLL_MAX_MS = 10000;
  const POLL_GIVE_UP_MS = 5 * 60 * 1000;

  let memory = {};
  const read = () => {
    try {
      return { ...memory, ...JSON.parse(sessionStorage.getItem(KEY) || "{}") };
    } catch {
      return memory;
    }
  };
  const save = (patch) => {
    memory = { ...read(), ...patch };
    try {
      sessionStorage.setItem(KEY, JSON.stringify(memory));
    } catch {
      /* in-memory fallback for this page view */
    }
    return memory;
  };
  const el = (tag, text, className) => {
    const node = document.createElement(tag);
    if (text) node.textContent = text;
    if (className) node.className = className;
    return node;
  };
  const button = (text, className, onClick) => {
    const node = el("button", text, className);
    node.type = "button";
    if (onClick) node.addEventListener("click", onClick);
    return node;
  };
  // Every notice screen offers the same way back, styled as the page's primary action.
  const homeLink = () => {
    const link = el("a", "Return to moses", SUBMIT_CLASS);
    link.href = config.root;
    return link;
  };

  const completionActions = () => {
    const actions = el("div", "", "onboarding-completion-actions");
    const account = el("a", "Visit account center", SUBMIT_CLASS);
    account.href = config.accountCenterUrl || "https://account.moses.com";
    account.rel = "noreferrer";
    const shop = el("a", "See more moses products", "onboarding-link t-body-small");
    shop.href = config.shopAllUrl || "https://moses.com/collections/all";
    shop.rel = "noreferrer";
    actions.append(account, shop);
    return actions;
  };

  // The capability arrives once in the URL. Keep it for this tab and take it out of the
  // address bar so it does not ride along in screenshots, referrers or later page views.
  const sameCapability = (a, b) =>
    Boolean(a && b) && a.token === b.token && a.signingToken === b.signingToken && a.sessionId === b.sessionId && (a.patientCaseId || null) === (b.patientCaseId || null);
  function captureCapability() {
    const params = new URLSearchParams(location.search);
    const signingToken = params.get("signing_token") || undefined;
    const token = params.get("token") || undefined;
    const sessionId = params.get("session_id") || undefined;
    if (token || sessionId || signingToken) {
      const capability = { token, sessionId, signingToken, ...(params.get("patient_case_id") ? { patientCaseId: params.get("patient_case_id") } : {}) };
      const current = read();
      if (!sameCapability(current.capability, capability)) {
        memory = {};
        try {
          sessionStorage.removeItem(KEY);
        } catch {
          /* ignore */
        }
      }
      save({ capability });
      params.delete("signing_token");
      params.delete("token");
      params.delete("session_id");
      const search = params.toString();
      try {
        history.replaceState(
          history.state,
          "",
          location.pathname + (search ? `?${search}` : "") + location.hash,
        );
      } catch {
        /* ignore */
      }
    }
    return read().capability || null;
  }
  const capabilityQuery = (capability) => {
    const params = new URLSearchParams();
    if (capability.token) params.set("token", capability.token);
    if (capability.patientCaseId) params.set("patient_case_id", capability.patientCaseId);
    if (capability.sessionId) params.set("session_id", capability.sessionId);
    return params.toString();
  };
  const capabilityBody = (capability) => ({
    token: capability.token,
    session_id: capability.sessionId,
    ...(capability.patientCaseId ? { patient_case_id: capability.patientCaseId } : {}),
  });

  // Backbone's funnel keys ↔ this tab's flow keys, in one table: the context we read, the
  // draft we autosave and the final submit all map through it. Checkout owns the email, so
  // the context supplies it but an intake body never sends it back.
  const FIELDS = [
    ["first_name", "firstName"],
    ["last_name", "lastName"],
    ["email", "email"],
    ["date_of_birth", "dateOfBirth"],
    ["patient_phone", "phone"],
    ["patient_relationship", "patientRelationship"],
    ["treatment_date_unknown", "treatmentUnknown"],
    ["booking_contact_name", "bookingContactName"],
    ["booking_contact_role", "bookingContactRole"],
    ["clinic_lookup_requested", "clinicLookupRequested"],
    ["scan_recollection", "scanRecollection"],
    ["clinic_record_name", "clinicRecordName"],
    ["retainer_requests", "retainerRequests"],
    ["treatment_month", "treatmentMonth"],
    ["treatment_year", "treatmentYear"],
    ["retainer_frequency", "wearsRetainer"],
    ["clinic_name", "clinicName"],
    ["clinic_address", "clinicAddress"],
    ["clinic_phone", "clinicPhone"],
    ["clinic_email", "clinicEmail"],
    ["clinic_website", "clinicWebsite"],
    ["google_place_id", "clinicPlaceId"],
  ];
  const CONTEXT_ONLY = new Set(["email"]);
  const contextToFlow = (ctx) => {
    const patch = {};
    for (const [key, flowKey] of FIELDS) {
      if ((typeof ctx[key] === "string" && ctx[key]) || typeof ctx[key] === "boolean") patch[flowKey] = ctx[key];
    }
    return patch;
  };
  const intakeBody = (capability, flow) => {
    const body = capabilityBody(capability);
    for (const [key, flowKey] of FIELDS) {
      if (!CONTEXT_ONLY.has(key)) body[key] = flow[flowKey] ?? undefined;
    }
    if (flow.treatmentUnknown) { delete body.treatment_month; delete body.treatment_year; }
    return body;
  };

  const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  const currentYear = new Date().getFullYear();
  const YEARS = Array.from({ length: currentYear - 1899 }, (_, i) => String(currentYear - i));
  // Stored verbatim by the backend; keep these strings byte-identical to Backbone's.
  const RETAINER_OPTIONS = [
    "Every night",
    "A few nights a week",
    "A couple nights a month",
    "Stopped wearing it",
    "Don't have one anymore",
  ];

  // Google Places (New) over REST, shared with the conference intake (assets/quiz-form.js).
  const placeSuggestions = (input, sessionToken, signal) =>
    shared.places.suggestions(config.placesKey, input, sessionToken, signal);
  const placeDetails = (placeId, sessionToken) => shared.places.details(config.placesKey, placeId, sessionToken);

  // Client-generated idempotency key so re-clicks, retries and reloads never create a second
  // authorization. Scoped to the capability, as Backbone's site did, so a fresh link cannot
  // reuse a finished run's key from the same tab.
  async function capabilityScope(capability) {
    const value = `${capability.signingToken || capability.token || capability.sessionId || ""}:${capability.patientCaseId || ""}`;
    const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
    return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("");
  }
  const longDate = (value) =>
    new Date(value || Date.now()).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  class MosesOnboarding extends HTMLElement {
    connectedCallback() {
      this.capability = captureCapability();
      this.state = read();
      this.state.flow ||= {};
      this.state.step = Math.min(TOTAL_STEPS, Math.max(1, Number(this.state.step) || 1));
      this.ctx = null;
      this.controller?.abort();
      this.controller = new AbortController();
      this.onVisibility = () => {
        if (!document.hidden && this.pollPaused) void this.pollNow();
      };
      document.addEventListener("visibilitychange", this.onVisibility);
      this.render("loading");
      void this.resolve();
    }
    disconnectedCallback() {
      this.controller?.abort();
      clearTimeout(this.pollTimer);
      document.removeEventListener("visibilitychange", this.onVisibility);
    }
    persist(patch = {}) {
      this.state = save({ ...this.state, ...patch });
    }

    // ── Gate ──────────────────────────────────────────────────────────────────
    async resolve() {
      if (!this.capability) {
        this.render("invalid");
        return;
      }
      track("onboarding_started", {});
      try {
        const ctx = await this.fetchContext();
        this.apply(ctx);
      } catch (error) {
        if (error.name === "AbortError") return;
        // 404/403 = definitively not a usable link. Anything else (network, 5xx) is transient:
        // show the form rather than block, exactly as the previous page did.
        if (error.status === 404 || error.status === 403) this.render("invalid");
        else this.render("unavailable");
      }
    }
    fetchContext() {
      if (this.capability.signingToken) return request(`/v1/onboarding/signing/${encodeURIComponent(this.capability.signingToken)}`, { signal: this.controller.signal });
      return request(`/v1/onboarding/context?${capabilityQuery(this.capability)}`, {
        signal: this.controller.signal,
      });
    }
    localPreview() {
      const page = new URL(location.href);
      return Boolean(config.devPreview && ["127.0.0.1", "localhost"].includes(page.hostname));
    }
    previewCompleted() {
      if (!this.localPreview()) return new Set();
      const page = new URL(location.href);
      const ids = new Set((page.searchParams.get("preview_completed") || "").split(",").filter(value => /^[0-9a-f-]{36}$/i.test(value)).slice(0, 20));
      if (page.searchParams.get("preview_step") === "after-signing" && this.capability?.patientCaseId)
        ids.add(this.capability.patientCaseId);
      return ids;
    }
    signedPreview() {
      const page = new URL(location.href);
      return this.localPreview() && (page.searchParams.get("preview_step") === "after-signing" || this.previewCompleted().has(this.capability?.patientCaseId));
    }
    apply(ctx) {
      this.ctx = ctx;
      if (ctx.signing_only) {
        this.state.flow = contextToFlow(ctx);
        this.state.step = ctx.status === "signed" ? TOTAL_STEPS : 4;
        this.ctx.capabilities = { representative_signing: true };
        this.render(ctx.status === "signed" ? "done" : "form");
        return;
      }
      if (ctx.patient_case_id) { this.capability.patientCaseId = ctx.patient_case_id; save({ capability: this.capability }); }
      if (ctx.status === "expired" || ctx.status === "revoked") {
        this.render("expired");
        return;
      }
      if (ctx.status === "pending_payment") {
        this.render("pending");
        this.schedulePoll();
        return;
      }
      if (ctx.assignment_confirmed && this.signedPreview()) {
        this.render("signed-preview");
        return;
      }
      if (ctx.assignment_confirmed && ctx.patients?.length > 1) {
        const sequence = this.familyProgress();
        const selected = sequence.items.find(person => person.id === sequence.current);
        if (selected?.ready || (sequence.next && sequence.next.id !== sequence.current && !selected?.waiting)) {
          this.render("sequence");
          return;
        }
      }
      if (this.state.step === TOTAL_STEPS && this.state.signedAt) {
        this.render("done");
        return;
      }
      if (ctx.purchase_units?.length && !ctx.assignment_confirmed) {
        this.render("patients");
        return;
      }
      if (ctx.completed) {
        this.render("completed");
        return;
      }
      // A fresh tab resumes the deepest saved stage; an explicit Back in this tab wins.
      const hasLocalProgress = Boolean(this.state.flow.dateOfBirth);
      // Local edits made since the last autosave are newer than the server's draft.
      const flow = { ...contextToFlow(ctx), ...this.state.flow };
      delete flow.scanAvailabilityUrl;
      if (ctx.scan_availability_url) flow.scanAvailabilityUrl = ctx.scan_availability_url;
      if (!hasLocalProgress && ctx.resuming && ctx.assignment_confirmed) {
        this.state.step = flow.dateOfBirth && (ctx.patients?.length || phoneValid(flow.phone)) ? 2 : 1;
        if (this.state.step === 2 && (flow.treatmentUnknown || (flow.treatmentMonth && flow.treatmentYear)) && flow.wearsRetainer) this.state.step = 3;
        if (this.state.step === 3 && !flow.scanAvailabilityUrl && flow.clinicName) this.state.step = 4;
      }
      this.persist({ flow });
      if (ctx.signing_pending) { this.render("waiting-signature"); return; }
      if (ctx.clinic_lookup_requested) { this.render("clinic-help"); return; }
      this.render("form");
    }
    // A buyer can open the Thank-you CTA a beat before the paid webhook lands. Poll with
    // backoff, pause while the tab is hidden, and stop asking after five minutes.
    schedulePoll() {
      this.pollStarted ||= Date.now();
      if (Date.now() - this.pollStarted > POLL_GIVE_UP_MS) {
        this.render("pending-timeout");
        return;
      }
      const delay = Math.min(POLL_MAX_MS, POLL_START_MS * 1.5 ** (this.pollMisses || 0));
      clearTimeout(this.pollTimer);
      this.pollTimer = setTimeout(() => void this.pollNow(), delay);
    }
    async pollNow() {
      clearTimeout(this.pollTimer);
      if (document.hidden) {
        this.pollPaused = true;
        return;
      }
      this.pollPaused = false;
      if (this.polling) return;
      this.polling = true;
      try {
        const ctx = await this.fetchContext();
        if (ctx.status !== "pending_payment") {
          this.pollStarted = null;
          this.pollMisses = 0;
          this.apply(ctx);
          return;
        }
      } catch (error) {
        if (error.name === "AbortError") return;
      } finally {
        this.polling = false;
      }
      // Still pending, or a transient failure: wait a little longer and ask again.
      this.pollMisses = (this.pollMisses || 0) + 1;
      this.schedulePoll();
    }
    restartPoll() {
      this.pollStarted = Date.now();
      this.pollMisses = 0;
      this.render("pending");
      void this.pollNow();
    }

    // ── Rendering ─────────────────────────────────────────────────────────────
    render(screen, focus = true) {
      this.screen = screen;
      this.dataset.screen = screen;
      this.closest(".quiz-page")?.classList.toggle("is-authorization", screen === "form" && this.state.step === 4);
      this.replaceChildren();
      this.heading = el("h1", "", "quiz-heading");
      this.heading.id = "quiz-heading";
      this.heading.tabIndex = -1;
      this.description = el("p", "", "quiz-description");
      this.fields = el("div", "", "quiz-form");
      this.errorEl = el("p", "", "quiz-error");
      this.errorEl.hidden = true;
      this.errorEl.role = "alert";
      this.errorEl.tabIndex = -1;
      this.familyProgressEl = null;
      const family = this.ctx?.patients?.length > 1;
      if (!family && screen === "signed-preview") this.append(progress(TOTAL_STEPS, TOTAL_STEPS));
      else if (!family && (screen === "form" || screen === "done")) this.append(this.renderProgress());
      this.append(this.heading, this.description, this.fields, this.errorEl);
      if (config.devPreview) { const banner = el("p", screen === "signed-preview" ? "Preview · after signing" : this.previewCompleted().size ? "Preview · simulated completion" : "Preview · sample data", "onboarding-dev-note t-body-micro"); banner.title = "Development preview. Emails and calls are disabled."; this.prepend(banner); }
      if (family && ["form", "done", "completed", "signed-preview", "waiting-signature", "clinic-help", "sequence", "correction"].includes(screen)) {
        this.familyProgressEl = this.familyProgressHeader(screen === "done");
        this.heading.before(this.familyProgressEl);
      }
      const notices = {
        unavailable: () => this.notice("We couldn’t load your details.", "Your saved progress is safe. Try again to continue.", { action: ["Try again", () => this.resolve()] }),
        loading: () =>
          this.notice("One moment.", "We’re pulling up your order.", {
            list: ["Checking your link", "Finding your order", "Preparing your form"],
            done: 1,
          }),
        invalid: () =>
          this.notice(
            "This link isn’t valid.",
            "Open the link from your order confirmation email to pick up onboarding. If it still doesn’t work, reply to that email and we’ll help you finish.",
            { home: true, contact: true },
          ),
        expired: () =>
          this.notice(
            "This link has expired.",
            "For your security, onboarding links stop working after 30 days. Reply to our email and we’ll send you a fresh one.",
            { home: true, contact: true },
          ),
        pending: () =>
          this.notice(
            "Connecting your order",
            "Your checkout is complete. We’re preparing your onboarding form and this page will continue automatically.",
            {
              list: ["Payment confirmed", "Finding your order", "Preparing your form"],
              done: 1,
              status: "Usually a few seconds.",
            },
          ),
        "pending-timeout": () =>
          this.notice(
            "Still connecting your order",
            "Your payment went through, but your order is taking longer than usual to reach us. Try again in a moment, or keep this page open and reply to your order email if it doesn’t continue.",
            { action: ["Check again", () => this.restartPoll()], contact: true },
          ),
        completed: () => {
          if (!this.renderSignedFamilyConfirmation()) {
            this.notice(
              "You’re all set.",
              "Your authorization is signed. We’ll contact your clinic about the records you authorized.",
              {},
            );
            this.fields.append(completionActions(), contactLine(config.contactEmail));
          }
        },
      };
      if (notices[screen]) notices[screen]();
      else if (screen === "sequence") this.renderSignedFamilyConfirmation(false);
      else if (screen === "waiting-signature") this.renderAdultHandoff();
      else if (screen === "clinic-help") this.renderClinicHelp();
      else if (screen === "correction") this.renderWearerCorrection();
      else if (screen === "patients") this.renderPatientAssignment();
      else if (screen === "done" || screen === "signed-preview") this.renderDone();
      else this.renderStep();
      if (focus) this.heading.focus();
    }
    renderPatientAssignment() {
      const units = this.ctx.purchase_units;
      this.heading.className = "t-h1 onboarding-assignment-heading";
      this.heading.textContent = units.length === 1 ? "Who is this retainer for?" : "Who are these for?";
      this.description.textContent = units.length === 1 ? "Confirm who will wear it." : "Choose a person for each retainer.";
      this.assignmentDraft ||= units.map((unit, i) => ({ mode: i === 0 ? "Me" : "", first: "", last: "", relationship: "Someone else" }));
      this.assignmentPayer ||= { first: this.ctx.payer_suggestion?.first_name || "", last: this.ctx.payer_suggestion?.last_name || "" };
      let submit;
      const updateAssignment = () => {
        const missing = this.assignmentDraft.findIndex(d => !d.mode || !(d.mode === "Me" ? this.assignmentPayer : d).first.trim() || !(d.mode === "Me" ? this.assignmentPayer : d).last.trim());
        if (submit) submit.disabled = missing !== -1;
        this.fields.querySelectorAll(".onboarding-wearer-hint").forEach((hint,i) => { hint.textContent = !this.assignmentDraft[i].mode ? "Choose who will wear this retainer." : ""; });
      };
      units.forEach((unit, i) => {
        const draft = this.assignmentDraft[i];
        const section = el("section", "", "onboarding-wearer");
        const arches = { both: "Upper + lower", upper: "Upper only", lower: "Lower only" }[unit.arches];
        const title = el("h2", "", "onboarding-wearer-title t-body-small");
        title.append(el("span", `Retainer ${i + 1}`), el("span", arches, "text-off-grey"));
        section.append(title);
        const modes = ["Me", "Someone else"];
        section.append(this.choiceButtons({ label: `Who is retainer ${i + 1} for?`, value: draft.mode, items: modes, onChange: value => {
          draft.mode = value;
          this.render("patients", false);
          this.fields.querySelectorAll(".onboarding-wearer")[i].querySelector('button[aria-pressed="true"]')?.focus({ preventScroll: true });
        } }));
        if (draft.mode === "Me" || draft.mode === "Someone else") {
          const person = draft.mode === "Me" ? this.assignmentPayer : draft;
          const names = el("div", "", "onboarding-wearer-names");
          for (const [part, label, autocomplete] of [["first", "First name", "given-name"], ["last", "Last name", "family-name"]]) {
            const field = this.field({ id: `wearer-${part}-${i}`, label, value: person[part], autocomplete, onInput: input => { person[part] = input.value; updateAssignment(); } });
            field.className = "onboarding-name-field";
            field.querySelector("span").className = "t-body-micro text-off-grey";
            field.querySelector("input").className = "quiz-field";
            names.append(field);
          }
          section.append(names);
          if (draft.mode === "Someone else") section.append(this.choiceButtons({ label: "Relationship", showLabel: true, value: draft.relationship, items: ["My child", "My spouse", { value: "Someone else", label: "Other" }], onChange: value => { draft.relationship = value; } }));
        }
        section.append(el("p", "", "onboarding-wearer-hint t-body-micro text-off-grey"));
        this.fields.append(section);
      });
      submit = button("Confirm and continue", SUBMIT_CLASS, async () => {
        const patients = new Map();
        const keys = [];
        for (let i = 0; i < units.length; i++) {
          const draft = this.assignmentDraft[i];
          if (!draft.mode) return this.error(`Choose who retainer ${i + 1} is for.`);
          let key;
          if (draft.mode.startsWith("Same person")) {
            const source = Number(draft.mode.match(/\d+$/)?.[0]) - 1;
            key = keys[source];
            if (!key) return this.error("Choose a wearer for the earlier retainer first.");
          } else {
            key = draft.mode === "Me" ? "payer" : `wearer-${i}`;
            const person = draft.mode === "Me" ? this.assignmentPayer : draft;
            if (!person.first.trim() || !person.last.trim()) return this.error(`Enter the wearer’s name for retainer ${i + 1}.`);
            patients.set(key, { key, first_name: person.first.trim(), last_name: person.last.trim(), relationship: draft.mode === "Me" ? "self" : draft.relationship === "My child" ? "child" : draft.relationship === "My spouse" ? "spouse" : "other" });
          }
          keys.push(key);
        }
        submit.disabled = true; this.error("");
        try {
          await request("/v1/onboarding/patients", { method: "POST", body: { ...capabilityBody(this.capability), patients: [...patients.values()], assignments: units.map((unit, i) => ({ unit_id: unit.id, patient_key: keys[i] })) }, signal: this.controller.signal });
          this.state = { capability: this.capability, step: 1, flow: {} };
          save(this.state);
          this.apply(await this.fetchContext());
        } catch (error) { this.error(error.message || MESSAGES.generic); submit.disabled = false; }
      });
      this.fields.append(submit);
      updateAssignment();
    }

    familyProgress(currentSigned = false) {
      const current = this.capability?.patientCaseId || this.ctx?.patient_case_id || this.state.orderId;
      const assumed = this.previewCompleted();
      const items = (this.ctx?.patients || []).map(person => {
        const ready = Boolean(person.completed || person.availability_saved || assumed.has(person.id) || (currentSigned && person.id === current));
        return { ...person, ready, waiting: !ready && Boolean(person.signing_pending || person.clinic_lookup_requested) };
      });
      const next = items.find(person => !person.ready && !person.waiting);
      return { current, items, next, received: items.filter(person => person.ready).length, allDone: items.length > 0 && items.every(person => person.ready) };
    }
    familyProgressHeader(currentSigned = false) {
      const sequence = this.familyProgress(currentSigned);
      const section = el("section", "", "onboarding-family-progress-header");
      section.setAttribute("aria-label", "Onboarding progress");
      const bar = progress(sequence.received, sequence.items.length);
      bar.children[0].textContent = `${sequence.received} of ${sequence.items.length} complete`;
      bar.setAttribute("role", "progressbar");
      bar.setAttribute("aria-label", "People who have completed onboarding");
      bar.setAttribute("aria-valuemin", "0");
      bar.setAttribute("aria-valuemax", String(sequence.items.length));
      bar.setAttribute("aria-valuenow", String(sequence.received));
      section.append(bar);
      const person = sequence.items.find(person => person.id === sequence.current && !person.ready && !person.waiting && person.id === sequence.next?.id && this.screen !== "sequence") || sequence.next;
      const waiting = sequence.items.find(person => person.waiting);
      const label = sequence.allDone ? "Everyone’s done"
        : person ? `${person.id === sequence.current && this.screen !== "sequence" ? "For" : "Next"}: ${person.name}`
        : waiting ? `${waiting.name} · ${waiting.signing_pending ? "Waiting for their signature" : "Clinic details needed"}` : "";
      if (label) section.append(el("p", label, "t-body-small text-off-grey"));
      return section;
    }
    renderProgress() {
      if (this.ctx?.signing_only) return el("p", "Authorization", "onboarding-family-progress t-body-small text-off-grey");
      if (this.ctx?.patients?.length) {
        const index = this.ctx.patients.findIndex(p => p.id === this.ctx.patient_case_id);
        const step = ["", "Details", "Treatment", "Scan", "Authorization", "Submitted"][this.state.step] || "Details";
        this.progress = el("p", `Person ${index + 1} of ${this.ctx.patients.length} · ${step}`, "onboarding-family-progress t-body-small text-off-grey");
        return this.progress;
      }
      this.progress = progress(this.state.step, TOTAL_STEPS);
      return this.progress;
    }
    // The bordered checklist with blush markers: the gate notices' progress.
    checklist(label, items, done = 0) {
      const ol = el("ol", "", "quiz-list");
      ol.setAttribute("aria-label", label);
      items.forEach((text, index) => {
        const row = el("li");
        if (index < done) row.setAttribute("data-done", "");
        row.append(el("span", "", "quiz-marker"), el("span", text));
        ol.append(row);
      });
      return ol;
    }
    notice(heading, body, { list, done = 0, status, action, home, contact } = {}) {
      this.heading.textContent = heading;
      this.description.textContent = body;
      if (list) this.fields.append(this.checklist(heading, list, done));
      if (status) {
        const p = el("p", status, "onboarding-status");
        p.role = "status";
        this.fields.append(p);
      }
      if (action) this.fields.append(button(action[0], SUBMIT_CLASS, action[1]));
      if (home) this.fields.append(homeLink());
      if (contact) this.fields.append(contactLine(config.contactEmail));
    }
    error(message) {
      this.errorEl.textContent = message || "";
      this.errorEl.hidden = !message;
      if (message) this.errorEl.focus();
    }

    // ── Shared controls ───────────────────────────────────────────────────────
    // One labelled row of the bordered field stack, around whichever control it holds.
    row(id, label, control, hint) {
      const row = el("label", "", "quiz-row");
      row.htmlFor = id;
      control.id = id;
      control.name = id;
      control.className = "quiz-input";
      row.append(el("span", label, "quiz-label"), control);
      if (hint) row.append(el("span", hint, "quiz-hint"));
      return row;
    }
    field({ id, label, type = "text", value = "", placeholder = "", autocomplete, inputMode, hint, min, max, onInput }) {
      const input = el("input");
      input.type = type;
      input.value = value;
      if (placeholder) input.placeholder = placeholder;
      if (autocomplete) input.autocomplete = autocomplete;
      if (inputMode) input.inputMode = inputMode;
      if (min) input.min = min;
      if (max) input.max = max;
      input.addEventListener("input", () => onInput?.(input));
      return this.row(id, label, input, hint);
    }
    stack(rows, pair = false) {
      const group = el("div", "", pair ? "quiz-stack is-pair" : "quiz-stack");
      group.append(...rows);
      return group;
    }
    select({ id, label, value = "", options, placeholder, onChange }) {
      const select = el("select");
      const blank = el("option", placeholder);
      blank.value = "";
      select.append(blank);
      for (const option of options) {
        const node = el("option", option);
        node.value = option;
        node.selected = option === value;
        select.append(node);
      }
      select.addEventListener("change", () => onChange(select.value));
      return this.row(id, label, select);
    }
    choiceButton(text, onClick) {
      const template = document.getElementById("onboarding-choice-template");
      const classes = template.content.firstElementChild.className;
      const node = button("", `${classes} onboarding-choice`, onClick);
      node.append(el("span", text, "t-body-small t-trim"));
      return node;
    }
    choiceButtons({ label, items, value, onChange, showLabel = false }) {
      const group = el("fieldset", "", "onboarding-choice-group");
      group.append(el("legend", label, showLabel ? "onboarding-choice-label t-body-micro text-off-grey" : "quiz-sr"));
      const choices = el("div", "", `onboarding-choice-grid${items.length > 3 ? " is-many" : ""}`);
      for (const item of items) {
        const key = typeof item === "string" ? item : item.value;
        const text = typeof item === "string" ? typographic(item) : item.label;
        const node = this.choiceButton(text, () => {
          choices.querySelectorAll("button").forEach(option => option.setAttribute("aria-pressed", String(option === node)));
          onChange(key);
        });
        node.setAttribute("aria-pressed", String(key === value));
        choices.append(node);
      }
      group.append(choices);
      return group;
    }
    options({ legend, items, value, onChange }) {
      return this.choiceButtons({ label: legend, items, value, onChange });
    }
    linkButton(text, action) {
      return button(text, "onboarding-link", action);
    }
    navButton(text, action, back) {
      const node = button("", back ? "quiz-back" : "quiz-next", action);
      const arrow = el("img");
      arrow.src = config.arrow;
      arrow.alt = "";
      arrow.width = 22;
      arrow.height = 8;
      const label = el("span", text);
      node.append(...(back ? [arrow, label] : [label, arrow]));
      return node;
    }
    nav({ back = true, next = "Continue", onNext } = {}) {
      const nav = el("nav", "", "quiz-nav");
      nav.setAttribute("aria-label", "Onboarding navigation");
      if (back) nav.append(this.navButton("Back", () => this.back(), true));
      else nav.append(el("span"));
      if (next) {
        this.next = this.navButton(next, onNext, false);
        nav.append(this.next);
      }
      return nav;
    }
    setValid(valid) {
      if (this.next) this.next.disabled = !valid;
    }
    // A step's answers in one scratch object. Every edit persists as typed, so a reload
    // resumes the step, then clears the error and re-checks Continue. `keys` maps the step's
    // own names to the flow keys the draft and the intake body are built from.
    stepDraft(keys, isValid) {
      const draft = {};
      for (const key of Object.keys(keys)) draft[key] = this.state.flow[keys[key]] || "";
      const valid = () => isValid(draft);
      const update = (key, value, patch) => {
        draft[key] = value;
        this.persist({ flow: { ...this.state.flow, [keys[key]]: value, ...patch } });
        this.error("");
        this.setValid(valid());
      };
      return { draft, valid, update };
    }
    back() {
      if (this.state.step > 1) {
        this.persist({ step: this.state.step - 1 });
        void transition(this, "back", () => this.render("form"));
      }
    }
    // Move on: persist the step's answers, autosave a draft (best effort), show the next step.
    advance(patch, stepName) {
      if (patch.clinicLookupRequested === false && this.ctx) {
        this.ctx.clinic_lookup_requested = false;
        const patient = this.ctx.patients?.find(person => person.id === this.ctx.patient_case_id);
        if (patient) patient.clinic_lookup_requested = false;
      }
      this.persist({ flow: { ...this.state.flow, ...patch }, step: this.state.step + 1 });
      track("onboarding_step_completed", { step: this.state.step - 1, step_name: stepName });
      void transition(this, "forward", () => this.render("form"));
      void this.saveDraft();
    }
    // Silent and best effort: the final submit persists everything.
    async saveDraft() {
      try {
        await request("/v1/onboarding/draft", {
          method: "POST",
          body: intakeBody(this.capability, this.state.flow),
          signal: this.controller.signal,
        });
      } catch {
        /* nothing to show; the answers are still in this tab */
      }
    }

    // ── Steps ─────────────────────────────────────────────────────────────────
    renderStep() {
      const renderers = {
        1: () => this.renderStep1(),
        2: () => this.renderStep2(),
        3: () => this.renderStep3(),
        4: () => this.renderStep4(),
      };
      const renderer = renderers[this.state.step];
      if (renderer) renderer();
      else {
        this.persist({ step: 1 });
        this.renderStep1();
      }
    }
    renderFamilyDetails() {
      const ctx = this.ctx, flow = this.state.flow;
      this.persist({ flow: { ...flow, bookingContactRole: "purchaser", bookingContactName: ctx.purchaser_name || "", phone: ctx.purchaser_phone || "" } });
      this.heading.textContent = `${this.patientName}’s details.`;
      this.description.textContent = "Use the wearer’s date of birth.";
      if (ctx.patient_editable) this.fields.append(this.linkButton("Change person", () => this.render("correction")));
      const valid = () => Boolean(this.state.flow.dateOfBirth);
      this.fields.append(this.stack([this.field({
        id: "date-of-birth", label: `${this.patientName}’s date of birth`, type: "date",
        value: flow.dateOfBirth || "", autocomplete: "bday", min: "1900-01-01", max: new Date().toISOString().slice(0,10),
        onInput: input => {
          this.persist({ flow: { ...this.state.flow, dateOfBirth: input.value } });
          this.setValid(valid());
        },
      })]));
      this.fields.append(el("p", "Scan updates go to the person who placed the order.", "t-body-small text-off-grey"));
      this.fields.append(this.nav({back:false,onNext:()=>{if(valid())this.advance({...this.state.flow},"patient_details");}}));
      this.setValid(valid());
    }
    renderWearerCorrection() {
      this.heading.textContent = "Who will wear this retainer?";
      this.description.textContent = "Changing the wearer clears their unsigned intake. The purchaser and payment stay the same.";
      const current = this.ctx.patients?.find(p=>p.id === this.ctx.patient_case_id);
      this.correctionDraft ||= {mode:current?.relationship === "self" ? "Me" : "Someone else",first:this.state.flow.firstName || "",last:this.state.flow.lastName || "",relationship:current?.relationship || "other"};
      const draft = this.correctionDraft;
      this.fields.append(this.choiceButtons({label:"Who is this for?",items:["Me","Someone else"],value:draft.mode,onChange:value=>{draft.mode=value;draft.first=value === "Me" ? this.ctx.payer_suggestion?.first_name || "" : "";draft.last=value === "Me" ? this.ctx.payer_suggestion?.last_name || "" : "";draft.relationship=value === "Me" ? "self" : "other";this.render("correction");}}));
      let submit;
      const rows = [this.field({id:"correct-first",label:"First name",value:draft.first,onInput:input=>{draft.first=input.value;submit.disabled=!draft.first.trim() || !draft.last.trim();}}),this.field({id:"correct-last",label:"Last name",value:draft.last,onInput:input=>{draft.last=input.value;submit.disabled=!draft.first.trim() || !draft.last.trim();}})];
      this.fields.append(this.stack(rows,true));
      if(draft.mode === "Someone else")this.fields.append(this.choiceButtons({label:"Relationship",showLabel:true,items:[{value:"child",label:"My child"},{value:"spouse",label:"My spouse"},{value:"other",label:"Other"}],value:draft.relationship,onChange:value=>{draft.relationship=value;}}));
      submit=button("Save wearer",SUBMIT_CLASS,async()=>{
        submit.disabled=true;const cap={...this.capability};
        try { await request("/v1/onboarding/patient-correction",{method:"POST",body:{...capabilityBody(cap),key:"corrected",first_name:draft.first.trim(),last_name:draft.last.trim(),relationship:draft.mode === "Me" ? "self" : draft.relationship},signal:this.controller.signal});
          if(!sameCapability(cap,this.capability))return;
          this.correctionDraft=null;this.state={capability:this.capability,step:1,flow:{}};save(this.state);this.apply(await this.fetchContext());
        }catch(error){this.error(error.message || MESSAGES.generic);submit.disabled=false;}
      });submit.disabled=!draft.first.trim() || !draft.last.trim();
      this.fields.append(submit,this.linkButton("Keep current wearer",()=>{this.correctionDraft=null;this.render("form");}));
    }
    renderAdultHandoff() {
      const name=this.patientName;
      this.heading.textContent=`${name} needs to sign.`;
      this.description.textContent="Patients aged 18 or older sign their own authorization. Give them a personal link to review the saved details and sign.";
      const slot=el("div","","onboarding-sign-link");
      const showLink=url=>{
        slot.replaceChildren();
        const label=el("label","Signing link","onboarding-name-field t-body-small");
        const input=el("input","","quiz-field");input.value=url;input.readOnly=true;input.setAttribute("aria-label","Personal signing link");label.append(input);slot.append(label);
        const copy=button("Copy signing link",SUBMIT_CLASS,async()=>{try{await navigator.clipboard.writeText(url);copy.textContent="Link copied";}catch{input.focus();input.select();copy.textContent="Select and copy the link";}});
        const open=el("a",`Open ${name}’s signing page`,"onboarding-link");open.href=url;open.target="_blank";open.rel="noopener noreferrer";
        slot.append(copy,open,el("p","The link expires in 30 days. Nothing has been sent.","t-body-small text-off-grey"));
      };
      const create=button(this.ctx.signing_pending ? "Get signing link" : "Create signing link",SUBMIT_CLASS,async()=>{
        create.disabled=true;const cap={...this.capability};const flow={...this.state.flow};
        try{
          // Pin the link only after the latest visible intake is committed.
          await request("/v1/onboarding/draft",{method:"POST",body:intakeBody(cap,flow),signal:this.controller.signal});
          if(!sameCapability(cap,this.capability))return;
          const result=await request("/v1/onboarding/signing-links",{method:"POST",body:capabilityBody(cap),signal:this.controller.signal});
          if(!sameCapability(cap,this.capability))return;
          this.persist({flow:{...this.state.flow,signingLink:result.url}});this.ctx.signing_pending=true;const patient=this.ctx.patients?.find(p=>p.id === this.ctx.patient_case_id);if(patient)patient.signing_pending=true;this.render("waiting-signature");
        }catch(error){this.error(error.message || MESSAGES.generic);create.disabled=false;}
      });
      if(this.ctx.signing_pending && this.state.flow.signingLink)showLink(this.state.flow.signingLink);else slot.append(create);
      this.fields.append(slot);
      if(this.ctx.patients?.length>1 && this.ctx.signing_pending){const title=this.heading.textContent,description=this.description.textContent;this.renderSignedFamilyConfirmation(false);this.heading.textContent=title;this.description.textContent=description;}
      else if(this.ctx.signing_pending) this.fields.append(el("p","Waiting for their signature. You can return to this order link to check progress.","t-body-small text-off-grey"),this.linkButton("Check signing status",async()=>{try{this.apply(await this.fetchContext());}catch(error){this.error(error.message);}}));
      this.fields.append(this.linkButton("Back to clinic details",()=>{this.persist({step:3});this.render("form");}));
    }
    renderStep1() {
      if (this.ctx?.assignment_confirmed) return this.renderFamilyDetails();
      const ctx = this.ctx || {};
      const known = Boolean(ctx.email);
      const haveName = Boolean(ctx.first_name && ctx.last_name) && !ctx.patient_editable;
      const greet = (ctx.first_name || this.state.flow.firstName || "").trim();
      const assigned = Boolean(ctx.assignment_confirmed);
      const wearer = [ctx.first_name, ctx.last_name].filter(Boolean).join(" ");
      if (assigned) {
        this.heading.textContent = wearer ? `${wearer}’s details.` : "Wearer details.";
        this.description.textContent = this.forSomeoneElse ? "Enter their date of birth and a contact number for arranging the scan." : "Enter your date of birth and a contact number for arranging the scan.";
      } else {
        this.heading.textContent = ctx.resuming ? "Welcome back" : "One more step";
        if (greet) {
          const name = el("span", greet);
          name.setAttribute("data-ph-mask", "");
          this.heading.append(", ", name);
        }
        this.heading.append(".");
        this.description.textContent = ctx.resuming
          ? "We saved your progress. Pick up where you left off."
          : "We need a couple of details to start making your retainer.";
      }
      const { draft, valid, update } = this.stepDraft(
        {
          email: "email", firstName: "firstName", lastName: "lastName",
          dateOfBirth: "dateOfBirth", phone: "phone", relationship: "patientRelationship",
        },
        (answers) =>
          (known || emailValid(answers.email)) &&
          (haveName || (answers.firstName.trim() && answers.lastName.trim())) &&
          Boolean(answers.dateOfBirth) &&
          phoneValid(answers.phone),
      );
      if (known && !assigned) {
        const box = el("div", "", "onboarding-account");
        box.append(el("small", ctx.patient_editable || ctx.purchaser_name ? "Purchased by" : "Your account"));
        const fullName = ctx.purchaser_name || [ctx.first_name, ctx.last_name].filter(Boolean).join(" ");
        box.append(el("span", fullName || ctx.email));
        if (fullName) box.append(el("span", ctx.email));
        this.fields.append(box);
      }
      const rows = [];
      if (ctx.patient_editable && !ctx.assignment_confirmed) {
        this.fields.append(this.choiceButtons({ label: "This retainer is for", showLabel: true, value: ({self:"Me",child:"My child",other:"Someone else"})[draft.relationship] || "Me", items: ["Me", "My child", "Someone else"], onChange: value => update("relationship", ({"Me":"self","My child":"child","Someone else":"other"})[value]) }));
      }
      if (!known)
        rows.push(
          this.field({
            id: "email", label: "Email", type: "email", value: draft.email,
            placeholder: "jane@example.com", autocomplete: "email",
            onInput: (input) => update("email", input.value),
          }),
        );
      if (!haveName)
        rows.push(
          this.field({
            id: "first-name", label: "First name", value: draft.firstName,
            autocomplete: "given-name",
            onInput: (input) => update("firstName", input.value),
          }),
          this.field({
            id: "last-name", label: "Last name", value: draft.lastName,
            autocomplete: "family-name",
            onInput: (input) => update("lastName", input.value),
          }),
        );
      rows.push(
        this.field({
          id: "date-of-birth", label: "Date of birth", type: "date", value: draft.dateOfBirth,
          autocomplete: "bday",
          // A bounded year makes Chrome advance after four digits instead of accepting "200112".
          min: "1900-01-01", max: new Date().toISOString().slice(0, 10),
          onInput: (input) => update("dateOfBirth", input.value),
        }),
        this.field({
          id: "phone", label: assigned ? "Contact phone" : "Phone number", type: "tel", value: draft.phone,
          placeholder: "(555) 000-0000", autocomplete: "tel", inputMode: "tel",
          onInput: (input) => {
            input.value = formatPhone(input.value);
            update("phone", input.value);
          },
        }),
      );
      this.fields.append(this.stack(rows));
      if (assigned && ctx.purchaser_name) {
        this.fields.append(el("p", `Purchased by ${ctx.purchaser_name}`, "onboarding-purchase-contact t-body-micro text-off-grey"));
      }
      this.fields.append(
        this.nav({
          back: false,
          onNext: () => {
            if (!known && !emailValid(draft.email)) return this.error("Please enter a valid email address.");
            if (!phoneValid(draft.phone)) return this.error("Please enter a valid phone number.");
            if (!valid()) return this.error("Please fill in every field.");
            this.advance(
              {
                dateOfBirth: draft.dateOfBirth,
                phone: draft.phone.trim(),
                ...(ctx.patient_editable ? { patientRelationship: draft.relationship || "self" } : {}),
                ...(known ? {} : { email: draft.email.trim() }),
                ...(haveName ? {} : { firstName: draft.firstName.trim(), lastName: draft.lastName.trim() }),
              },
              "patient_details",
            );
          },
        }),
      );
      this.setValid(valid());
    }
    get forSomeoneElse() {
      const patient = this.ctx?.patients?.find(person => person.id === this.ctx.patient_case_id);
      const relationship = patient?.relationship || this.state.flow.patientRelationship;
      return Boolean(relationship && relationship !== "self");
    }
    renderStep2() {
      this.heading.textContent = "Treatment details";
      this.description.textContent = "An approximate date is fine. If you don’t remember, choose “I’m not sure”.";
      const { draft, valid, update } = this.stepDraft(
        { month: "treatmentMonth", year: "treatmentYear", retainer: "wearsRetainer" },
        (answers) => Boolean(((answers.month && answers.year) || this.state.flow.treatmentUnknown) && answers.retainer),
      );
      const when = el("div");
      when.append(el("p", this.forSomeoneElse ? "When did they finish treatment?" : "When did you finish treatment?", "quiz-question"));
      when.append(
        this.stack(
          [
            this.select({
              id: "treatment-month", label: "Month", value: draft.month, options: MONTHS,
              placeholder: "Choose", onChange: (value) => update("month", value),
            }),
            this.select({
              id: "treatment-year", label: "Year", value: draft.year, options: YEARS,
              placeholder: "Choose", onChange: (value) => update("year", value),
            }),
          ],
          true,
        ),
      );
      const unsure = this.linkButton(this.state.flow.treatmentUnknown ? "I remember the date" : "I’m not sure of the date", () => {
        this.persist({ flow: { ...this.state.flow, treatmentUnknown: !this.state.flow.treatmentUnknown, ...(this.state.flow.treatmentUnknown ? {} : { treatmentMonth: "", treatmentYear: "" }) } });
        this.render("form");
      });
      when.append(unsure);
      if (this.state.flow.treatmentUnknown) { when.querySelector(".quiz-stack")?.remove(); when.append(el("p", "We’ll ask the clinic to help confirm when treatment finished.", "t-body-small text-off-grey")); }
      const how = el("div");
      const question = this.forSomeoneElse ? "How often do they wear their retainer?" : "How often do you wear your retainer?";
      how.append(el("p", question, "quiz-question"));
      how.append(
        this.options({
          name: "retainer-frequency", legend: question, items: RETAINER_OPTIONS,
          value: draft.retainer, onChange: (value) => update("retainer", value),
        }),
      );
      this.fields.append(
        when,
        how,
        this.nav({
          onNext: () => {
            if (!valid()) return this.error("Please answer both questions.");
            this.advance(
              { treatmentMonth: this.state.flow.treatmentUnknown ? "" : draft.month, treatmentYear: this.state.flow.treatmentUnknown ? "" : draft.year, treatmentUnknown: Boolean(this.state.flow.treatmentUnknown), wearsRetainer: draft.retainer },
              "treatment_details",
            );
          },
        }),
      );
      if (this.ctx?.capabilities?.scan_recollection) {
        const requestField = el("label", "", "onboarding-name-field");
        requestField.htmlFor = "retainer-requests";
        requestField.append(el("span", "Anything else we should know? (optional)", "t-body-small"));
        const textarea = el("textarea", "", "quiz-field");
        textarea.id = "retainer-requests";
        textarea.name = "retainer-requests";
        textarea.rows = 3;
        textarea.maxLength = 2000;
        textarea.value = this.state.flow.retainerRequests || "";
        textarea.placeholder = this.forSomeoneElse ? "Share any requests or details about their retainer." : "Share any requests or details about your retainer.";
        textarea.addEventListener("input", () => this.persist({ flow: { ...this.state.flow, retainerRequests: textarea.value } }));
        requestField.append(textarea);
        this.fields.insertBefore(requestField, this.fields.lastElementChild);
      }
      this.setValid(valid());
    }
    renderStep3() {
      const availability = this.ctx?.scan_availability_url || this.state.flow.scanAvailabilityUrl;
      if (availability) return this.renderAvailabilityHandoff(availability);
      if (["definitely_not", "impressions"].includes(this.state.flow.scanRecollection)) {
        return this.renderNewScan(this.state.flow.scanRecollection === "impressions" ? "I only have physical impressions or molds" : "I did not have a digital scan after treatment");
      }
      if (this.ctx?.capabilities?.scan_recollection && !this.state.flow.scanRecollection) return this.renderScannerQuestion();
      this.heading.textContent = `${this.patientName}’s clinic.`;
      this.description.textContent = this.forSomeoneElse ? "We’ll contact the clinic for their 3D digital scan and complete patient chart." : "We’ll contact them for your 3D digital scan and complete patient chart.";
      const searchable = Boolean(config.placesKey) && !this.placesUnavailable;
      if (this.clinicManual === undefined) this.clinicManual = !searchable;
      const manual = this.clinicManual || !searchable;
      if (manual) this.fields.append(el("p", "Enter the clinic’s name. A town or address helps us find the right one.", "t-body-small text-off-grey"));
      const { draft, valid, update } = this.stepDraft(
        {
          name: "clinicName", address: "clinicAddress", phone: "clinicPhone",
          email: "clinicEmail", website: "clinicWebsite", placeId: "clinicPlaceId",
        },
        (answers) => Boolean(answers.name.trim()),
      );
      const autofilled = Boolean(draft.name);
      // Typing over the clinic name drops the Places id that filled it.
      const updateName = (value) => {
        draft.placeId = "";
        draft.phone = "";
        draft.email = "";
        draft.website = "";
        update("name", value, { clinicPlaceId: "", clinicPhone: "", clinicEmail: "", clinicWebsite: "" });
      };
      const detailRows = () => [
        this.field({ id: "clinic-name", label: "Clinic name", value: draft.name, autocomplete: "organization",
          onInput: (input) => updateName(input.value) }),
        this.field({ id: "clinic-address", label: "Town or address (optional)", value: draft.address, autocomplete: "street-address",
          onInput: (input) => update("address", input.value) }),
      ];
      if (!manual) {
        const search = el("div");
        const input = el("input");
        input.type = "search";
        input.placeholder = "Clinic name";
        input.autocomplete = "off";
        input.setAttribute("aria-autocomplete", "list");
        input.setAttribute("aria-controls", "clinic-suggestions");
        const row = this.row("clinic-search", "Search your clinic", input);
        const list = el("ul", "", "onboarding-suggestions");
        list.id = "clinic-suggestions";
        list.role = "listbox";
        list.setAttribute("aria-label", "Clinic suggestions");
        list.hidden = true;
        search.append(this.stack([row]), list);
        this.placesSession ||= crypto.randomUUID();
        let timer;
        let inflight;
        const show = (suggestions) => {
          list.replaceChildren();
          list.hidden = suggestions.length === 0;
          for (const suggestion of suggestions) {
            const item = el("li");
            item.role = "presentation";
            const option = button("", "", () => void choose(suggestion));
            option.role = "option";
            option.append(el("span", suggestion.main || suggestion.text));
            if (suggestion.secondary) option.append(el("small", suggestion.secondary));
            item.append(option);
            list.append(item);
          }
        };
        const choose = async (suggestion) => {
          input.value = suggestion.text;
          show([]);
          let details = {};
          try {
            details = await placeDetails(suggestion.placeId, this.placesSession);
          } catch {
            /* fall back to the suggestion text */
          }
          this.placesSession = crypto.randomUUID();
          this.persist({
            flow: {
              ...this.state.flow,
              clinicName: details.displayName?.text || suggestion.main || suggestion.text,
              clinicAddress: details.formattedAddress || suggestion.secondary || "",
              clinicPhone: details.nationalPhoneNumber || "",
              clinicEmail: "",
              clinicWebsite: details.websiteUri || "",
              clinicPlaceId: suggestion.placeId,
            },
          });
          this.render("form", false);
          this.querySelector("#clinic-name")?.focus();
        };
        input.addEventListener("input", () => {
          clearTimeout(timer);
          inflight?.abort();
          const value = input.value.trim();
          if (!value) return show([]);
          timer = setTimeout(async () => {
            inflight = new AbortController();
            try {
              show(await placeSuggestions(value, this.placesSession, inflight.signal));
            } catch (error) {
              if (error.name === "AbortError") return;
              show([]);
              if (error.status === 403 || error.status === 429 || error.status === 400) {
                this.persist({flow:{...this.state.flow,clinicName:value,clinicPlaceId:"",clinicPhone:"",clinicEmail:"",clinicWebsite:""}});
                this.placesUnavailable = true;
                this.clinicManual = true;
                this.render("form", false);
                this.querySelector("#clinic-name")?.focus();
              }
            }
          }, 300);
        });
        this.fields.append(search);
        if (autofilled) this.fields.append(this.stack(detailRows()));
        this.fields.append(
          this.linkButton("Can’t find your clinic? Enter it manually", () => {
            this.persist({flow:{...this.state.flow,clinicName:input.value.trim() || this.state.flow.clinicName || "",clinicPlaceId:""}});
            this.clinicManual = true;
            this.render("form", false);
            this.querySelector("#clinic-name")?.focus();
          }),
        );
      } else {
        this.fields.append(this.stack(detailRows()));
        if (searchable)
          this.fields.append(
            this.linkButton("Search by clinic name instead", () => {
              this.clinicManual = false;
              this.render("form", false);
              this.querySelector("#clinic-search")?.focus();
            }),
          );
      }
      this.fields.append(
        this.nav({
          onNext: () => {
            if (!valid())
              return this.error(
                manual ? "Please enter the clinic’s name." : "Please choose your clinic.",
              );
            track("onboarding_clinic_entry", { autofilled, used_places_api: !manual });
            this.advance(
              {
                clinicLookupRequested: false,
                clinicName: draft.name.trim(),
                clinicAddress: draft.address.trim(),
                clinicPhone: draft.phone.trim(),
                clinicEmail: draft.email.trim(),
                clinicWebsite: draft.website.trim(),
                clinicPlaceId: draft.placeId,
              },
              "clinic",
            );
          },
        }),
      );
      this.setValid(valid());
      if (this.ctx?.capabilities?.scan_recollection) {
        const nameField = this.field({ id: "clinic-record-name", label: "Full name on dental records", value: this.state.flow.clinicRecordName || "", autocomplete: "off", onInput: input => { this.persist({ flow: { ...this.state.flow, clinicRecordName: input.value } }); } });
        const nameDetails = el("details", "", "onboarding-scanner-help t-body-small");
        nameDetails.append(el("summary", "Dental records under a different name? (optional)"), el("p", "For example, a legal name, maiden name or previous surname. This helps the clinic find the right records.", "t-body-small text-off-grey"), this.stack([nameField]));
        nameDetails.open = Boolean(this.state.flow.clinicRecordName);
        this.fields.insertBefore(nameDetails, this.fields.lastElementChild);
      }
      if (this.ctx?.capabilities?.representative_signing && manual) {
        this.fields.append(this.linkButton("I don’t remember which clinic", async () => {
          const flow = {...this.state.flow,clinicName:draft.name.trim(),clinicAddress:draft.address.trim(),clinicPhone:draft.phone.trim(),clinicEmail:draft.email.trim(),clinicLookupRequested:true};
          try {
            await request("/v1/onboarding/draft",{method:"POST",body:intakeBody(this.capability,flow),signal:this.controller.signal});
            this.persist({flow});this.ctx.clinic_lookup_requested=true;const person=this.ctx.patients?.find(p=>p.id === this.ctx.patient_case_id);if(person)person.clinic_lookup_requested=true;this.render("clinic-help");
          } catch(error) { this.error(error.message || MESSAGES.generic); }
        }));
      }
      if (this.ctx?.capabilities?.new_scan_request) this.fields.append(this.linkButton(this.forSomeoneElse ? `I know ${this.patientName} needs a new scan` : "I know I need a new scan", () => this.renderNewScan()));
    }
    renderClinicHelp() {
      this.heading.textContent = `We’ll help find ${this.patientName}’s clinic.`;
      this.description.textContent = `We’ll contact ${this.state.flow.bookingContactName || "you"} to help identify the clinic. The authorization still needs to be signed once we know which practice to contact.`;
      this.fields.append(el("p","Clinic help requested · onboarding still pending","t-body-small text-off-grey"));
      const next = this.familyProgress().next;
      if(next) this.fields.append(this.familyNext(next));
      this.fields.append(this.linkButton("Add clinic details now",()=>this.render("form")));
    }
    renderScannerQuestion() {
      this.fields.classList.add("onboarding-scanner-form");
      this.heading.textContent = this.forSomeoneElse ? "Do they remember a wand like this?" : "Do you remember a wand like this?";
      this.description.textContent = this.forSomeoneElse
        ? "A dentist uses this handheld scanner to create a 3D model of their teeth. We’re looking for a scan taken after treatment."
        : "A dentist uses this handheld scanner to create a 3D model of your teeth. We’re looking for a scan taken after treatment.";
      if (config.scannerImage) {
        const image = el("img", "", "onboarding-scanner-image");
        image.src = config.scannerImage;
        image.alt = "Example of an intraoral scanning wand beside a 3D image of teeth";
        image.width = 1480; image.height = 1086;
        this.fields.append(image);
      }
      const help = el("details", "", "onboarding-scanner-help t-body-small");
      help.append(el("summary", "Not sure what kind of scan it was?"));
      help.append(el("p", this.forSomeoneElse
        ? "Did they have Invisalign, or does their orthodontist make replacement retainers for them? That clinic may have a scan on file. If unsure, choose “Not sure” and we’ll check."
        : "Did you have Invisalign, or does your orthodontist make replacement retainers for you? That clinic may have a scan on file. If unsure, choose “Not sure” and we’ll check.", "onboarding-scanner-copy"));
      help.append(el("p", "A tray filled with putty is a physical impression. Scanners can look different from the example above.", "onboarding-scanner-copy"));
      const choices = [
        ["Yes, after treatment", "yes"], ["Not sure", "not_sure"],
        ["Definitely not", "definitely_not"], ["Only physical impressions", "impressions"],
      ];
      const options = el("div", "", "onboarding-scan-choices");
      for (const [label, value] of choices) options.append(this.choiceButton(label, () => {
        this.persist({ flow: { ...this.state.flow, scanRecollection: value } });
        if (value === "definitely_not" || value === "impressions") {
          this.beginNewScan(value === "impressions" ? "I only have physical impressions or molds" : "I did not have a digital scan after treatment");
        } else this.render("form");
      }));
      this.fields.append(options, help);
    }

    availabilityFlowUrl(url) {
      const target = new URL(url);
      if (!["http:", "https:"].includes(target.protocol) || !target.pathname.startsWith("/availability/")) {
        throw new Error("We couldn’t open availability. Please try again.");
      }
      const flow = new URLSearchParams(target.hash.slice(1));
      if (this.capability.token) flow.set("onboarding_token", this.capability.token);
      else if (this.capability.sessionId) flow.set("onboarding_session", this.capability.sessionId);
      const completed = this.previewCompleted();
      if (completed.size) flow.set("preview_completed", [...completed].join(","));
      target.hash = flow.toString();
      return target.href;
    }

    renderAvailabilityHandoff(url) {
      const target = new URL(this.availabilityFlowUrl(url));
      if (!["http:", "https:"].includes(target.protocol) || !target.pathname.startsWith("/availability/")) {
        throw new Error("We couldn’t open availability. Please try again.");
      }
      this.heading.textContent = "Choose scan times.";
      this.description.textContent = "Your scan request is saved. Share times over the next three weeks that work for the appointment.";
      this.fields.replaceChildren();
      const link = el("a", "Continue to availability", SUBMIT_CLASS);
      link.href = target.href;
      link.rel = "noreferrer";
      this.fields.append(link);
    }

    async beginNewScan(reason) {
      if (this.scanRequestPending) return;
      this.scanRequestPending = true;
      const capability = { ...this.capability };
      const fields = this.fields;
      fields.querySelectorAll("button").forEach(node => { node.disabled = true; });
      fields.setAttribute("aria-busy", "true");
      const status = el("p", "Opening availability…", "onboarding-scan-help");
      status.role = "status";
      fields.append(status);
      this.error("");
      try {
        await this.saveDraft();
        if (!sameCapability(capability, this.capability)) return;
        const result = await request("/v1/onboarding/new-scan-request", { method: "POST", body: { ...capabilityBody(capability), reason }, signal: this.controller.signal });
        if (!sameCapability(capability, this.capability)) return;
        const availabilityUrl = this.availabilityFlowUrl(result.availability_url);
        this.renderAvailabilityHandoff(availabilityUrl);
        this.persist({ flow: { ...this.state.flow, scanAvailabilityUrl: availabilityUrl } });
        try { location.assign(availabilityUrl); } catch { /* The visible link remains usable if navigation is blocked. */ }
      } catch (error) {
        status.remove();
        if (sameCapability(capability, this.capability)) this.error(error.message || MESSAGES.generic);
      } finally {
        this.scanRequestPending = false;
        fields.removeAttribute("aria-busy");
        fields.querySelectorAll("button").forEach(node => { node.disabled = false; });
      }
    }

    renderNewScan(preset = "") {
      this.heading.textContent = "Request a new scan.";
      this.description.textContent = "If your clinic may still have a usable digital scan, we can check with them first. Tell us what you already know.";
      this.fields.replaceChildren();
      let reason = preset;
      this.fields.append(this.select({ id: "new-scan-reason", label: "Why do you need a new scan?", value: preset, placeholder: "Choose", options: [...(preset ? [preset] : []), "My office confirmed they don’t have a digital scan", "My teeth have changed since my last scan", "I only have physical impressions or molds", "Another reason"], onChange: value => { reason = value; submit.disabled = !reason; } }));
      const submit = button("Share availability", SUBMIT_CLASS, () => {
        if (reason) void this.beginNewScan(reason);
      });
      submit.disabled = !reason;
      this.fields.append(el("p", "Next, share your availability for the next three weeks.", "onboarding-scan-help"), submit, this.linkButton("Back to your clinic", () => this.render("form")));
    }
    // ── Step 4: authorization ─────────────────────────────────────────────────
    get docVersion() { return this.ctx?.authorization_document_version || config.docVersion; }
    get patientName() { return `${this.state.flow.firstName || this.ctx?.first_name || ""} ${this.state.flow.lastName || this.ctx?.last_name || ""}`.trim() || "the patient"; }
    get guardianRequired() { const dob = this.state.flow.dateOfBirth; if (!dob) return Boolean(this.ctx?.requires_guardian); const now = new Date(), born = new Date(`${dob}T12:00:00`); return now.getFullYear() - born.getFullYear() - (now.getMonth() < born.getMonth() || (now.getMonth() === born.getMonth() && now.getDate() < born.getDate()) ? 1 : 0) < 18; }
    loadDocument() {
      if (this.loadedDocumentVersion !== this.docVersion) { this.documentPromise = null; this.loadedDocumentVersion = this.docVersion; }
      this.documentPromise ||= request(`/v1/authorization-documents/${encodeURIComponent(this.docVersion)}`, {
        signal: this.controller.signal,
      }).catch((error) => {
        this.documentPromise = null;
        throw error;
      });
      return this.documentPromise;
    }
    renderDocument(doc, signatureUrl, signerName) {
      const flow = this.state.flow;
      const details = el("details", "", "onboarding-authorization-details");
      const summary = el("summary", "", "onboarding-document-heading");
      const toggle = el("span", "", "onboarding-document-toggle");
      toggle.append(
        el("span", "Show full text", "is-closed"),
        el("span", "Hide full text", "is-open"),
        el("span", "↓", "is-arrow"),
      );
      toggle.lastChild.setAttribute("aria-hidden", "true");
      summary.append(
        el("span", "Read the full authorization"),
        toggle,
      );
      details.append(summary);
      const box = el("div", "", "onboarding-document");
      box.setAttribute("aria-label", doc.title);
      const today = longDate();
      box.append(el("p", doc.title, "is-title"));
      const authority = el("p", doc.authority.prefix);
      authority.append(el("u", doc.authority.citation), doc.authority.suffix);
      const intro = el("p", doc.intro.prefix);
      intro.append(el("u", doc.intro.recipient), doc.intro.suffix);
      box.append(authority, intro);
      for (const section of doc.sections) {
        box.append(el("p", section.heading, "is-section"));
        switch (section.kind) {
          case "patient": {
            const line = `${flow.firstName || ""} ${flow.lastName || ""}`.trim() + ` · DOB: ${flow.dateOfBirth || ""}` + (flow.phone ? ` · ${flow.phone}` : "");
            box.append(el("div", line, "is-box"));
            break;
          }
          case "clinic":
            box.append(el("div", `${flow.clinicName || ""}${flow.clinicAddress ? ` · ${flow.clinicAddress}` : ""}`, "is-box"));
            break;
          case "list": {
            const ul = el("ul");
            for (const item of section.items) ul.append(el("li", item));
            box.append(ul);
            if (section.note) box.append(el("p", section.note, "is-note"));
            break;
          }
          case "text":
            box.append(el("div", section.text, "is-box"));
            break;
          case "paragraph":
            box.append(el("p", section.text));
            break;
          case "terms": {
            const ol = el("ol");
            for (const item of section.items) {
              const li = el("li");
              li.append(el("u", item.title), ` ${item.text}`);
              ol.append(li);
            }
            box.append(ol);
            break;
          }
          case "signature": {
            const wrap = el("div", "", "is-box");
            const sig = el("div", "", "is-signature");
            if (signatureUrl) {
              const img = el("img");
              img.src = signatureUrl;
              img.alt = "Signature";
              sig.append(img);
            } else sig.textContent = section.placeholder;
            const meta = el("div", "", "is-meta");
            meta.append(el("span", `${section.name_label} ${signerName || "—"}`), el("span", `${section.date_label} ${today}`));
            wrap.append(sig, meta);
            if (doc.signing && this.signerMetadata) {
              const guardian = this.signerMetadata.signer_capacity === "guardian";
              wrap.append(el("p", guardian ? (this.signerMetadata.signer_relationship === "parent" ? "Capacity: Parent" : "Capacity: Legal guardian") : "Capacity: Patient"));
              wrap.append(el("p", guardian ? doc.signing.guardian_declaration : doc.signing.patient_declaration));
            }
            box.append(wrap);
            break;
          }
        }
      }
      box.append(el("p", doc.footer.replace("{date}", today), "is-footer"));
      details.append(box);
      return details;
    }
    renderStep4() {
      const flow = this.state.flow;
      this.heading.textContent = "Your records, your authorization.";
      this.description.textContent = "To make your retainer, we need a 3D digital scan of your teeth. With your permission, we’ll ask your clinic for the scan and your complete patient chart.";
      const representative = this.ctx?.capabilities?.representative_signing;
      if (representative && this.forSomeoneElse && !this.guardianRequired && !this.ctx.signing_only) return this.renderAdultHandoff();
      const guardian = representative && this.guardianRequired;
      let signerName = guardian ? (flow.guardianName || this.ctx.purchaser_name || "") : this.patientName;
      this.signerMetadata = representative ? { signer_capacity: guardian ? "guardian" : "patient", signer_relationship: guardian ? (flow.guardianRelationship || "parent") : undefined, authority_confirmed: false } : {};
      this.heading.textContent = guardian ? `Sign for ${this.patientName}.` : `${this.patientName}’s authorization.`;
      this.description.textContent = `We’ll request ${this.patientName}’s scan and complete patient chart from ${flow.clinicName || "the clinic"}. The scan is used to make the retainer; treatment history helps our team review fit. A usable scan can move ahead while the chart follows.`;
      if (this.ctx.signing_only) {
        this.fields.append(el("p", `Patient: ${this.patientName} · Date of birth: ${flow.dateOfBirth || "—"}`, "t-body-small"));
        this.fields.append(el("p", "Details incorrect? Ask the purchaser to update them and share a new link before you sign.", "t-body-small text-off-grey"));
      }
      const documentRegion = el("section", "", "onboarding-document-wrap");
      const documentSlot = el("div");
      documentRegion.append(documentSlot);
      const loading = el("p", "Loading the authorization document.", "onboarding-status");
      loading.role = "status";
      documentSlot.append(loading);
      let doc = null;
      let signatureUrl = "";
      let consented = false;
      const valid = () => Boolean(doc && signatureUrl && consented && signerName.trim().length > 1 && !this.submitting);
      const refresh = () => {
        submit.disabled = !valid();
        if (doc) {
          const expanded = documentSlot.querySelector("details")?.open || false;
          const rendered = this.renderDocument(doc, signatureUrl, signerName);
          rendered.open = expanded;
          documentSlot.replaceChildren(rendered);
        }
      };
      const showDocument = () => {
        this.loadDocument().then(
          (loaded) => {
            if (loaded.doc_version !== this.docVersion) {
              const message = "The authorization document did not match this form. Please reload the page and try again.";
              documentSlot.replaceChildren(el("p", message, "onboarding-status"));
              return this.error(message);
            }
            doc = loaded;
            refresh();
          },
          (error) => {
            if (error.name === "AbortError") return;
            const retry = this.linkButton("Try loading the document again", () => {
              documentSlot.replaceChildren(loading);
              showDocument();
            });
            documentSlot.replaceChildren(retry);
            this.error("We couldn’t load the authorization document. Check your connection and try again.");
          },
        );
      };
      showDocument();
      // Signature
      const signature = el("div", "", "onboarding-signature");
      const labelRow = el("div", "", "onboarding-signature-label");
      const clear = button("Clear");
      labelRow.append(el("h2", "Sign here"), clear);
      const signingHelp = el("p", guardian ? "Use your own signature as the patient’s parent or legal guardian." : "Draw your signature in the box below.", "onboarding-signing-help");
      const padWrap = el("div", "", "onboarding-pad");
      const pad = el("moses-signature-pad");
      pad.setAttribute("role", "img");
      pad.setAttribute("aria-label", "Signature pad. Draw your signature with your finger or mouse.");
      padWrap.append(pad, el("span", "Your signature", "onboarding-pad-hint"));
      const signing = el("p", "Signing as ", "onboarding-signing");
      signing.append(el("span", signerName || "—"));
      if (guardian) {
        signature.append(this.stack([this.field({ id: "guardian-name", label: "Your full name", value: signerName, autocomplete: "name", onInput: input => { signerName = input.value; signing.lastChild.textContent = signerName || "—"; this.persist({ flow: { ...this.state.flow, guardianName: signerName } }); refresh(); } })]));
        signature.append(this.choiceButtons({ label: `Your relationship to ${this.patientName}`, showLabel: true, items: [{value:"parent",label:"Parent"},{value:"legal_guardian",label:"Legal guardian"}], value: this.signerMetadata.signer_relationship, onChange: value => { this.signerMetadata.signer_relationship = value; this.persist({ flow: { ...this.state.flow, guardianRelationship:value } }); refresh(); } }));
      }
      signature.append(labelRow, signingHelp, padWrap, signing);
      pad.addEventListener("signature:change", () => {
        signatureUrl = pad.toDataURL();
        padWrap.toggleAttribute("data-signed", Boolean(signatureUrl));
        this.error("");
        refresh();
      });
      clear.addEventListener("click", () => pad.clear());
      // Consent
      const consent = el("label", "", "quiz-option is-consent");
      const check = el("input");
      check.type = "checkbox";
      check.name = "consent";
      check.addEventListener("change", () => {
        consented = check.checked;
        if (representative) this.signerMetadata.authority_confirmed = check.checked;
        this.error("");
        refresh();
      });
      consent.append(
        check,
        el("span", `${representative ? (guardian ? `I am 18 or older and am ${this.patientName}’s parent or legal guardian with authority to sign. ` : `I am ${this.patientName} and am 18 or older. `) : ""}I have read the authorization and agree to sign electronically.`),
      );
      const submit = button("Sign and submit", SUBMIT_CLASS, () =>
        void this.submit({ pad, signerName, submit, signatureUrl: () => signatureUrl }),
      );
      submit.disabled = true;
      signature.append(consent, submit);
      this.fields.append(documentRegion, signature);
      if (!this.ctx.signing_only) this.fields.append(this.nav({ next: null }));
    }
    async submit({ pad, signerName, submit, signatureUrl }) {
      if (this.submitting) return;
      // Re-read the pad at submit time and fall back to the preview from the last stroke.
      const capability = { ...this.capability };
      const flow = { ...this.state.flow };
      const image = pad.toDataURL() || signatureUrl();
      if (!image) return this.error("Your signature was cleared. Please draw it again.");
      this.submitting = true;
      submit.disabled = true;
      submit.textContent = "Submitting";
      this.error("");
      try {
        const scope = await capabilityScope(capability);
        if (!sameCapability(capability, this.capability)) return;
        if (!this.state.idempotencyKey || this.state.idempotencyScope !== scope)
          this.persist({ idempotencyKey: crypto.randomUUID(), idempotencyScope: scope });
        const result = await request(this.capability.signingToken ? `/v1/onboarding/signing/${encodeURIComponent(this.capability.signingToken)}` : "/v1/onboarding", {
          method: "POST",
          signal: this.controller.signal,
          body: {
            ...(this.capability.signingToken ? {} : intakeBody(capability, flow)),
            ...this.signerMetadata,
            idempotency_key: this.state.idempotencyKey,
            signer_name: signerName,
            doc_version: this.docVersion,
            signature_image: image,
          },
        });
        if (!sameCapability(capability, this.capability)) return;
        this.persist({
          step: TOTAL_STEPS,
          orderId: result.order_id,
          signedAt: result.signed_at || new Date().toISOString(),
          signedDocVersion: this.docVersion,
          signerName,
        });
        try {
          const context = await this.fetchContext();
          if (!sameCapability(capability, this.capability)) return;
          this.ctx = context;
        } catch { /* The signature succeeded. Keep the confirmation usable without resubmitting it. */ }
        if (!sameCapability(capability, this.capability)) return;
        track("onboarding_step_completed", { step: 4, step_name: "authorization" });
        track("onboarding_completed", {});
        this.render("done");
      } catch (error) {
        if (error.name === "AbortError") return;
        this.error(error.message || MESSAGES.generic);
        submit.textContent = "Try again";
        submit.disabled = false;
      } finally {
        this.submitting = false;
      }
    }
    familyNext(patient) {
      const target = new URL(location.href);
      for (const key of ["token","session_id","signing_token","preview_step"]) target.searchParams.delete(key);
      if (this.capability.token) target.searchParams.set("token",this.capability.token);
      else if (this.capability.sessionId) target.searchParams.set("session_id",this.capability.sessionId);
      target.searchParams.set("patient_case_id",patient.id);
      const assumed = this.previewCompleted();
      if (assumed.size) target.searchParams.set("preview_completed",[...assumed].join(","));
      target.hash = "";
      const link = el("a",`Continue for ${patient.name}`,SUBMIT_CLASS); link.href = target.href; link.rel = "noreferrer"; return link;
    }
    renderSignedFamilyConfirmation(currentSigned = true) {
      const sequence = this.familyProgress(currentSigned);
      if (sequence.items.length < 2) return false;
      const waiting = sequence.items.filter(person => person.waiting);
      const needsClinic = waiting.some(person => person.clinic_lookup_requested);
      if (sequence.allDone) {
        this.heading.textContent = "That’s everyone.";
        this.description.textContent = "Everyone’s onboarding is complete. Scan appointments are confirmed separately.";
      } else if (sequence.next) {
        this.heading.textContent = `Next, ${sequence.next.name}.`;
        this.description.textContent = "Complete each person’s onboarding before continuing.";
      } else {
        this.heading.textContent = needsClinic ? "Clinic details are still needed." : "Waiting for a signature.";
        this.description.textContent = needsClinic
          ? "We’ll help identify the clinic. Everyone’s authorization or availability must be submitted before continuing."
          : "The remaining adult needs to sign their personal authorization before everyone’s onboarding is complete.";
      }
      if (!this.familyProgressEl) this.fields.append(this.familyProgressHeader(currentSigned));
      if (sequence.next) this.fields.append(this.familyNext(sequence.next));
      else if (!sequence.allDone) this.fields.append(this.linkButton("Check progress", async () => {
        try { this.apply(await this.fetchContext()); } catch (error) { this.error(error.message); }
      }));
      else this.fields.append(completionActions());
      return true;
    }

    // ── Step 5: confirmation ──────────────────────────────────────────────────
    renderDone() {
      if (this.ctx?.signing_only) { this.heading.textContent = `${this.patientName}’s authorization is saved.`; this.description.textContent = `We’ll ask ${this.state.flow.clinicName || "the clinic"} for the authorized records. There’s nothing else to sign here.`; this.fields.append(completionActions()); return; }
      if (this.renderSignedFamilyConfirmation()) return;
      const flow = this.state.flow;
      this.heading.textContent = "You’re all set.";
      this.description.textContent = `Your authorization was signed on ${longDate(this.state.signedAt)}. We’ll contact `;
      if (flow.clinicName) {
        const clinic = el("span", flow.clinicName);
        clinic.setAttribute("data-ph-mask", "");
        this.description.append(clinic);
      } else this.description.append("your clinic");
      const fullChart = this.state.signedDocVersion === "v1.2";
      this.description.append(fullChart ? " to collect your 3D digital scan and complete patient chart." : " about the records you authorized.");
      // Label and list travel together, like a question and its fields on the form steps.
      const next = el("div");
      next.append(
        el("p", "What happens next:", "quiz-question"),
        steps("What happens next", [
          "We send your signed authorization to your clinic",
          fullChart ? "Your clinic shares your 3D digital scan and complete patient chart" : "Your clinic shares the authorized records",
          "Your moses retainer is made and shipped",
        ]),
      );
      this.fields.append(next, completionActions(), contactLine(config.contactEmail));
    }
  }
  if (!customElements.get("moses-onboarding"))
    customElements.define("moses-onboarding", MosesOnboarding);
  // The tests (and a support session) drive this tab's saved state through here.
  window.MosesOnboarding = { read, save };
})();
